// Unit conversion + display helpers.
//
// **Storage rule (non-negotiable):** every weight in the system is persisted
// in kilograms, with up to 3 decimal places (i.e. gram-precision). Users who
// pick `unitsPreference: "lb"` see and type pounds; the conversion happens
// at the UI edge. Never store both.
//
// Why on-demand conversion matters: gym machines come in either unit
// (a leg-press in lb, a free-weight rack in kg). A user reading weight off
// a machine plate needs to enter what they see, not what they prefer to
// view in. The input field will offer a per-row "kg/lb" toggle even when
// the profile preference is the other unit, so we always convert and store
// in kg.
//
// The IPF/IWF conversion factor is 0.45359237 (the international avoirdupois
// pound, exact since 1959). Anything coarser (0.4536) accumulates >0.1 kg
// drift over a few hundred conversions.

const KG_PER_LB = 0.45359237;
const LB_PER_KG = 1 / KG_PER_LB;

export const lbToKg = (lb: number): number => lb * KG_PER_LB;
export const kgToLb = (kg: number): number => kg * LB_PER_KG;

/**
 * Round a weight to gram precision (3 decimals) — used as the canonical
 * round-trip rule when ingesting from any unit. Avoids accumulating
 * float artefacts across repeated conversions.
 */
export const roundKg = (kg: number): number => Math.round(kg * 1000) / 1000;

/**
 * Convert a user-entered weight (in any unit) to the canonical kg form
 * we persist. Pass the unit the user typed in, not their profile
 * preference — those can differ per row.
 */
export const toCanonicalKg = (value: number, unit: "kg" | "lb"): number =>
  roundKg(unit === "kg" ? value : lbToKg(value));

/**
 * Format a kg-stored weight for display in the user's preferred unit.
 * Returns a number, not a string — the i18n layer handles digit grouping
 * via Intl.NumberFormat at render time.
 */
export const fromCanonicalKg = (
  kg: number,
  display: "kg" | "lb",
): number => {
  if (display === "kg") return roundKg(kg);
  // Lb display rounds to 1 decimal — typical gym plate granularity is
  // 1.25 kg ≈ 2.756 lb, but most users think in 0.5-lb steps.
  return Math.round(kgToLb(kg) * 10) / 10;
};

/**
 * Pretty-print with the unit suffix. Locale-aware decimal separator via
 * Intl.NumberFormat. Pass the i18n locale (e.g. `i18n.resolvedLanguage`)
 * for the right formatting; default `"en-US"` for tests/SSR.
 *
 * `maxFractionDigits` overrides the default precision (kg keeps 3 so plate /
 * microplate math reads exactly; lb 1). Pass `0` for *derived estimates* like
 * lean mass, where 3 decimals is false precision (a BF%-driven value, inbox
 * 65d1a6ed). Real logged weights keep the default — don't lower it globally.
 */
export const formatWeight = (
  kg: number,
  display: "kg" | "lb",
  locale = "en-US",
  maxFractionDigits: number = display === "kg" ? 3 : 1,
): string => {
  const value = fromCanonicalKg(kg, display);
  const fmt = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  });
  return `${fmt.format(value)} ${display}`;
};

/**
 * Compact pretty-print for big tonnages (≥10k): "12,450 kg" → "12.5k kg".
 * Below the threshold falls through to formatWeight so small numbers
 * keep full precision. Used on the post-session share card where the
 * 9:16 PNG canvas can't fit 5-digit groups at the muscle-bar / footer
 * font sizes without overrunning the column.
 */
export const formatWeightCompact = (
  kg: number,
  display: "kg" | "lb",
  locale = "en-US",
  maxFractionDigits: number = display === "kg" ? 3 : 1,
): string => {
  const value = fromCanonicalKg(kg, display);
  if (Math.abs(value) < 10_000)
    return formatWeight(kg, display, locale, maxFractionDigits);
  const compact = Math.round(value / 100) / 10;
  const fmt = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  return `${fmt.format(compact)}k ${display}`;
};

/**
 * Pretty-print an aggregate training volume (Σ reps × weight, across a
 * set/exercise/session/muscle/week) in the user's preferred unit.
 *
 * Volume is a derived sum, not a scale reading or a plate weight —
 * `formatWeight`'s default precision (3 dp for kg, so a typed plate value
 * round-trips exactly) is false precision on a summed total, and worse,
 * it means the *same* number reads with a different decimal count in kg
 * (3 dp) than in lb (1 dp) depending on which of the volume callsites
 * formats it (DS-3). Volume always renders at 1 decimal, both units.
 * Falls through to the compact "12.5k kg" form above 10,000 via
 * `formatWeightCompact`.
 */
export const formatVolume = (
  kg: number,
  display: "kg" | "lb",
  locale = "en-US",
): string => formatWeightCompact(kg, display, locale, 1);

/**
 * Format a derived/estimated weight (e1RM, projected max, etc.) at
 * coarser precision than `formatWeight`. Epley has ~5% inherent error
 * so 3 decimals on an e1RM ("90.417 kg") is theatre — round to 1 dp.
 * Use this any time the displayed number didn't come from a scale or
 * a user typing on a plate; logged weights still go through formatWeight.
 */
export const formatE1rm = (
  kg: number,
  display: "kg" | "lb",
  locale = "en-US",
): string => {
  const raw = display === "kg" ? kg : kgToLb(kg);
  const rounded = Math.round(raw * 10) / 10;
  const fmt = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  return `${fmt.format(rounded)} ${display}`;
};

// Glucose. Canonical storage is mg/dL (what meters report, integer). Much of
// Europe — Switzerland included — reads mmol/L; convert at the UI edge, same
// rule as weight. Factor 18.0182 mg/dL per mmol/L (glucose molar mass
// 180.156 g/mol). Same single-source-of-truth posture: never store both.

export type GlucoseUnit = "mgdl" | "mmol";

const MGDL_PER_MMOL = 18.0182;

export const mmolToMgdl = (mmol: number): number => mmol * MGDL_PER_MMOL;
export const mgdlToMmol = (mgdl: number): number => mgdl / MGDL_PER_MMOL;

/** User-entered glucose (in the unit they typed) → canonical mg/dL (integer,
 *  matching how meters report). */
export const toCanonicalMgdl = (value: number, unit: GlucoseUnit): number =>
  Math.round(unit === "mgdl" ? value : mmolToMgdl(value));

/** mg/dL → a display number in the chosen unit (mmol/L to 1 dp, mg/dL int). */
export const fromCanonicalMgdl = (mgdl: number, display: GlucoseUnit): number =>
  display === "mgdl" ? Math.round(mgdl) : Math.round(mgdlToMmol(mgdl) * 10) / 10;

/** Pretty-print with the unit suffix, locale-aware decimal separator. */
export const formatGlucose = (
  mgdl: number,
  display: GlucoseUnit,
  locale = "en-US",
): string => {
  const value = fromCanonicalMgdl(mgdl, display);
  const fmt = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: display === "mgdl" ? 0 : 1,
  });
  return `${fmt.format(value)} ${display === "mgdl" ? "mg/dL" : "mmol/L"}`;
};
